// ─── BriefPreview ────────────────────────────────────────────────────────
// Pure rendering of a BriefEnvelope as a human-readable section.
// Used by ExamplesPanel (hover preview pane) and RunDetailPage (input params card).
// No fetching, no state — drives entirely from props.

import React from 'react';

const previewSnippet = (text, max = 280) => {
  if (!text) return '';
  const t = text.trim().replace(/\s+/g, ' ');
  return t.length > max ? t.slice(0, max - 1) + '…' : t;
};

export default function BriefPreview({ brief, t, color = '#6366F1' }) {
  if (!brief) {
    return (
      <div style={{
        color: t.textFaint, fontStyle: 'italic', lineHeight: 1.5,
        padding: '20px 0', textAlign: 'center',
      }}>
        Nessun brief disponibile.
      </div>
    );
  }

  const b = brief;
  const cp = b.content_params || {};
  const rs = b.research_sources || {};
  const kw = cp.keywords || {};
  const secondaryKw = Array.isArray(kw.secondary)
    ? kw.secondary.map(k => (typeof k === 'string' ? k : k?.keyword)).filter(Boolean)
    : [];
  const sourceLabels = [
    rs.use_company_website && 'Sito aziendale',
    rs.use_company_docs && 'Knowledge base',
    rs.use_web_search && 'Ricerca web',
    rs.use_uploaded_documents && 'Documenti caricati',
  ].filter(Boolean);
  const requestUrls = Array.isArray(rs.request_urls) ? rs.request_urls.filter(Boolean) : [];

  const sectionLabel = { fontWeight: 600, marginBottom: '4px', color: t.text };
  const sectionBody  = { marginBottom: '10px', color: t.textMuted, lineHeight: 1.5 };
  const chip = {
    display: 'inline-block', fontSize: '10px', fontFamily: "'JetBrains Mono', monospace",
    padding: '2px 8px', borderRadius: '100px',
    background: `${color}15`, color, letterSpacing: '0.4px',
    textTransform: 'uppercase', marginRight: '4px', marginBottom: '4px',
  };

  return (
    <>
      <div style={sectionLabel}>Argomento</div>
      <div style={sectionBody}>{b.topic || '—'}</div>

      {(b.platform || b.objective || b.persona) && (
        <div style={{ marginBottom: '10px' }}>
          {b.platform  && <span style={chip}>{b.platform}</span>}
          {b.objective && <span style={chip}>{b.objective}</span>}
          {b.persona   && <span style={chip}>{b.persona}</span>}
        </div>
      )}

      {(() => {
        const toneVal = cp.tone_hint || b.tone_hint;
        const rows = [
          toneVal             && ['Tone',          toneVal],
          cp.article_length   && ['Lunghezza',     cp.article_length],
          cp.seo_intent       && ['Intent SEO',    cp.seo_intent],
          cp.post_length      && ['Lunghezza post', cp.post_length],
          cp.cta_type         && ['Tipo CTA',      cp.cta_type],
          cp.slide_count      && ['Slide',         cp.slide_count],
          cp.email_length     && ['Lunghezza email', cp.email_length],
          cp.include_companion_post && ['Companion post', 'sì'],
          cp.ads_channel      && ['Canale ads',    cp.ads_channel],
          cp.ads_objective    && ['Obiettivo ads', cp.ads_objective],
          cp.ad_groups        && ['Ad groups',     cp.ad_groups],
          cp.ad_count         && ['Numero ads',    cp.ad_count],
          cp.output_style     && ['Stile output',  cp.output_style],
          cp.focus_area       && ['Focus area',    cp.focus_area],
          cp.headline_formula && ['Formula headline', cp.headline_formula],
        ].filter(Boolean);
        if (rows.length === 0) return null;
        return (
          <>
            <div style={sectionLabel}>Parametri</div>
            <div style={sectionBody}>
              {rows.map(([k, v]) => <div key={k}>{k}: {v}</div>)}
            </div>
          </>
        );
      })()}

      {(kw.primary || secondaryKw.length > 0) && (
        <>
          <div style={sectionLabel}>Keyword</div>
          <div style={sectionBody}>
            {kw.primary && <div><strong style={{ color: t.text }}>Primaria:</strong> {kw.primary}</div>}
            {secondaryKw.length > 0 && <div><strong style={{ color: t.text }}>Secondarie:</strong> {secondaryKw.join(', ')}</div>}
          </div>
        </>
      )}

      {Array.isArray(b.key_points) && b.key_points.length > 0 && (
        <>
          <div style={sectionLabel}>Punti chiave</div>
          <ul style={{ margin: '0 0 10px 0', paddingLeft: '18px', color: t.textMuted, lineHeight: 1.5 }}>
            {b.key_points.map((kp, i) => <li key={i}>{kp}</li>)}
          </ul>
        </>
      )}

      {b.additional_context && (
        <>
          <div style={sectionLabel}>Contesto aggiuntivo</div>
          <div style={sectionBody}>{previewSnippet(b.additional_context, 600)}</div>
        </>
      )}

      {(sourceLabels.length > 0 || rs.web_search_query || rs.kb_search_query || requestUrls.length > 0 || b.fact_check?.enabled) && (
        <>
          <div style={sectionLabel}>Fonti & ricerca</div>
          <div style={sectionBody}>
            {sourceLabels.length > 0 && (
              <div style={{ marginBottom: '4px' }}>
                {sourceLabels.map(s => <span key={s} style={chip}>{s}</span>)}
              </div>
            )}
            {rs.web_search_query && <div>Query web: <em>{rs.web_search_query}</em></div>}
            {rs.kb_search_query  && <div>Query KB: <em>{rs.kb_search_query}</em></div>}
            {requestUrls.length > 0 && (
              <div style={{ marginTop: '4px' }}>
                <div>URL richiesti:</div>
                <ul style={{ margin: '2px 0 0 0', paddingLeft: '18px' }}>
                  {requestUrls.map((u, i) => (
                    <li key={i} style={{ wordBreak: 'break-all' }}>
                      <a href={u} target="_blank" rel="noopener noreferrer" style={{ color: t.textMuted, textDecoration: 'underline' }}>{u}</a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {b.fact_check?.enabled && <div style={{ marginTop: '4px' }}><span style={chip}>Fact-check attivo</span></div>}
          </div>
        </>
      )}

      {b.cta && (
        <>
          <div style={sectionLabel}>CTA</div>
          <div style={{ color: t.textMuted, lineHeight: 1.5 }}>
            {b.cta}
            {b.cta_url && (
              <span style={{ color: t.textFaint, fontSize: '11px', display: 'block', marginTop: '2px' }}>
                → {b.cta_url}
              </span>
            )}
          </div>
        </>
      )}
    </>
  );
}
